import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Download, Calendar, Users, Search } from 'lucide-react';
import eventsService from '../services/eventsService';

const Events = () => {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchDate, setSearchDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (eventSlug && events.length > 0) {
      // First try to find by ID (for old URLs)
      let event = (events || []).find(e => e.id === eventSlug);
      
      // If not found by ID, try to find by slug (new format)
      if (!event) {
        event = (events || []).find(e => {
          const eventSlugFromName = e.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          return eventSlugFromName === eventSlug;
        });
      }
      
      // Always load the full event data from the individual event's index.json
      // to ensure we have all photos, not just the summary
      if (event) {
        loadSpecificEvent(event.folder || event.id);
      } else {
        // Try to load the specific event if not in the list
        loadSpecificEvent(eventSlug);
      }
    }
  }, [eventSlug, events]);

  const loadSpecificEvent = async (eventId) => {
    try {
      const event = await eventsService.getEvent(eventId);
      if (event) {
        setSelectedEvent(event);
      }
    } catch (error) {
      console.error('Error loading specific event:', error);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      
      // Load events from /events/ folder on Hostinger
      const fetchedEvents = await eventsService.getEvents();
      setEvents(Array.isArray(fetchedEvents) ? fetchedEvents : []);
      
      // Also save to localStorage for offline access
      if (fetchedEvents.length > 0) {
        eventsService.saveEventsToLocalStorage(fetchedEvents);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      
      // Fallback to localStorage if server fetch fails
      const fallbackEvents = eventsService.getEventsFromLocalStorage();
      setEvents(Array.isArray(fallbackEvents) ? fallbackEvents : []);
    } finally {
      setLoading(false);
    }
  };

  const downloadPhoto = (photo) => {
    const link = document.createElement('a');
    link.href = photo.url;
    link.download = photo.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openPhotoModal = (photo) => {
    setSelectedPhoto(photo);
    setShowPhotoModal(true);
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setShowPhotoModal(false);
  };

  const filteredEvents = (events || []).filter(event => 
    !searchDate || 
    event.date.includes(searchDate) || 
    event.name.toLowerCase().includes(searchDate.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a, #7c3aed, #ec4899)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p>Loading photo galleries...</p>
        </div>
      </div>
    );
  }

  // Individual Event View
  if (eventSlug && selectedEvent) {
    // Get all media (images and videos) - avoid duplicates
    let allMedia = [];
    const seenUrls = new Set();
    
    // Add images (prefer full URLs from images array)
    if (selectedEvent.images && Array.isArray(selectedEvent.images)) {
      selectedEvent.images.forEach((url, index) => {
        if (!seenUrls.has(url)) {
          allMedia.push({
            type: 'image',
            id: `img-${index}`,
            url: url,
            filename: url.split('/').pop() || `photo-${index + 1}.jpg`
          });
          seenUrls.add(url);
        }
      });
    }
    
    // Add custom images (only if not already seen)
    if (selectedEvent.customImages && Array.isArray(selectedEvent.customImages)) {
      const baseUrl = `${window.location.origin}/events/${selectedEvent.folder}`;
      selectedEvent.customImages.forEach((img, index) => {
        const fullUrl = img.url.startsWith('http') ? img.url : `${baseUrl}/${img.url}`;
        if (!seenUrls.has(fullUrl)) {
          allMedia.push({
            type: 'image',
            id: `custom-img-${index}`,
            url: fullUrl,
            filename: img.name || img.url
          });
          seenUrls.add(fullUrl);
        }
      });
    }
    
    // Add videos (avoid duplicates)
    if (selectedEvent.videos && Array.isArray(selectedEvent.videos)) {
      selectedEvent.videos.forEach((url, index) => {
        if (!seenUrls.has(url)) {
          allMedia.push({
            type: 'video',
            id: `video-${index}`,
            url: url,
            filename: url.split('/').pop() || `video-${index + 1}.mp4`
          });
          seenUrls.add(url);
        }
      });
    }
    
    // Add custom videos (only if not already seen)
    if (selectedEvent.customVideos && Array.isArray(selectedEvent.customVideos)) {
      const baseUrl = `${window.location.origin}/events/${selectedEvent.folder}`;
      selectedEvent.customVideos.forEach((vid, index) => {
        const fullUrl = vid.url.startsWith('http') ? vid.url : `${baseUrl}/${vid.url}`;
        if (!seenUrls.has(fullUrl)) {
          allMedia.push({
            type: 'video',
            id: `custom-video-${index}`,
            url: fullUrl,
            filename: vid.name || vid.url
          });
          seenUrls.add(fullUrl);
        }
      });
    }
    
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a, #7c3aed, #ec4899)',
        padding: window.innerWidth <= 768 ? '20px 16px' : '20px',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        {/* Event Info Section */}
        <div style={{
          maxWidth: '1200px',
          margin: window.innerWidth <= 768 ? '80px auto 40px' : '80px auto 40px',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: window.innerWidth <= 768 ? '20px 16px' : '30px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: 'white',
            margin: '0 0 10px 0',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}>
            {selectedEvent.name}
          </h1>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '30px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e5e7eb' }}>
              <Calendar size={20} />
              <span>{new Date(selectedEvent.date).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e5e7eb' }}>
                      <Users size={20} />
                      <span>{allMedia.length} {allMedia.length === 1 ? 'Item' : 'Items'} ({allMedia.filter(m => m.type === 'image').length} Photos, {allMedia.filter(m => m.type === 'video').length} Videos)</span>
                    </div>
          </div>
        </div>

        {/* Media Grid Section */}
        {allMedia.length > 0 && (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: window.innerWidth <= 768 ? '20px 16px' : '30px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '30px',
              textAlign: 'center',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}>
              Media Gallery
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {allMedia.map((media, index) => (
                <div
                  key={media.id}
                  onClick={() => openPhotoModal(media)}
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(236, 72, 153, 0.3)';
                    // Play video on hover
                    if (media.type === 'video') {
                      const video = e.currentTarget.querySelector('video');
                      if (video) video.play();
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    // Pause video on mouse leave
                    if (media.type === 'video') {
                      const video = e.currentTarget.querySelector('video');
                      if (video) {
                        video.pause();
                        video.currentTime = 0;
                      }
                    }
                  }}
                >
                  {media.type === 'image' ? (
                    <img
                      src={media.url}
                      alt={media.filename}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '250px',
                        objectFit: 'cover',
                        transition: 'all 0.3s ease'
                      }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjNDc0NzQ3Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg==';
                      }}
                    />
                  ) : (
                    <>
                      <video
                        src={media.url}
                        style={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'cover'
                        }}
                        muted
                        loop
                        playsInline
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(236, 72, 153, 0.8)',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}>
                        <Play size={20} color="white" />
                      </div>
                    </>
                  )}
                  
                  <div style={{
                    padding: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: 'white', fontSize: '14px' }}>
                      {media.type === 'video' ? '🎬' : '📸'} {media.filename}
                    </span>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPhoto(media);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Download size={14} />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media Modal */}
        {showPhotoModal && selectedPhoto && (
          <div
            onClick={closePhotoModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                position: 'relative'
              }}
            >
              {selectedPhoto.type === 'image' ? (
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: '10px'
                  }}
                />
              ) : (
                <video
                  src={selectedPhoto.url}
                  controls
                  autoPlay
                  style={{
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: '10px'
                  }}
                />
              )}
              
              <button
                onClick={closePhotoModal}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                right: '10px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '10px',
                borderRadius: '5px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                  {selectedPhoto.type === 'video' ? '🎬' : '📸'} {selectedPhoto.filename}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPhoto(selectedPhoto);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '8px 16px',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    margin: '0 auto'
                  }}
                >
                  <Download size={14} />
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Events List View (default)
  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #0c1445 0%, #1a1a2e 50%, #16213e 100%)',
      position: 'relative',
      overflow: 'auto',
      padding: '40px 20px'
    }}>
      {/* Night Sky Background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}>
        {/* Scrolling Moon */}
        <div style={{
          position: 'absolute',
          top: '8%',
          right: '12%',
          width: '160px',
          height: '160px',
          animation: 'moonScroll 20s linear infinite',
          zIndex: 1
        }}>
          <img 
            src="/moon.png" 
            alt="Moon" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 60px rgba(255, 255, 255, 0.3))',
              transition: 'filter 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.filter = 'drop-shadow(0 0 40px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 80px rgba(255, 255, 255, 0.4))';
            }}
            onMouseLeave={(e) => {
              e.target.style.filter = 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 60px rgba(255, 255, 255, 0.3))';
            }}
          />
        </div>
        
        {/* Milky Way Stars */}
        {Array.from({ length: 200 }, (_, i) => {
          const colors = ['white', '#ffd700', '#ff8c00', '#ffa500', '#ffff99', '#ffeb3b', '#ff9800'];
          const starColor = colors[i % colors.length];
          const size = i % 20 === 0 ? '3px' : i % 5 === 0 ? '2px' : '1px';
          const left = (i * 7.3) % 100;
          const top = (i * 11.7) % 100;
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: size,
                height: size,
                background: starColor,
                borderRadius: '50%',
                left: `${left}%`,
                top: `${top}%`,
                animation: `starSparkle ${2 + (i % 3)}s ease-in-out infinite`,
                animationDelay: `${i * 0.05}s`,
                boxShadow: `0 0 ${size === '3px' ? '8px' : '4px'} ${starColor}`,
                opacity: i % 3 === 0 ? 0.9 : 0.7
              }}
            />
          );
        })}
        
        {/* Multiple Shooting Stars with Random Properties */}
        {(() => {
          // Generate random shooting stars with random angles
          const generateRandomStar = (id) => {
            // Random start position (any edge of screen)
            const startEdge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
            let startX, startY, endX, endY;
            
            switch(startEdge) {
              case 0: // Start from top
                startX = Math.random() * 100;
                startY = -10;
                break;
              case 1: // Start from right
                startX = 110;
                startY = Math.random() * 100;
                break;
              case 2: // Start from bottom
                startX = Math.random() * 100;
                startY = 110;
                break;
              case 3: // Start from left
              default:
                startX = -10;
                startY = Math.random() * 100;
                break;
            }
            
            // Random end position (any edge of screen, but not same edge)
            let endEdge;
            do {
              endEdge = Math.floor(Math.random() * 4);
            } while (endEdge === startEdge);
            
            switch(endEdge) {
              case 0: // End at top
                endX = Math.random() * 100;
                endY = -10;
                break;
              case 1: // End at right
                endX = 110;
                endY = Math.random() * 100;
                break;
              case 2: // End at bottom
                endX = Math.random() * 100;
                endY = 110;
                break;
              case 3: // End at left
              default:
                endX = -10;
                endY = Math.random() * 100;
                break;
            }
            
            // Calculate angle for the star to face its direction
            const angle = Math.atan2(endY - startY, endX - startX) * (180 / Math.PI);
            
            const colorSets = [
              ['#ffffff', '#ffd700', '#ff8c00'],
              ['#ffffff', '#87ceeb', '#4169e1'],
              ['#ffffff', '#ff69b4', '#ff1493'],
              ['#ffffff', '#9333ea', '#7c3aed'],
              ['#ffffff', '#22c55e', '#16a34a']
            ];
            
            return {
              id,
              startX,
              startY,
              endX,
              endY,
              angle,
              duration: Math.random() * 2 + 3, // 3-5 seconds (much faster!)
              delay: Math.random() * 10, // 0-10 seconds
              size: Math.random() * 3 + 4, // 4-7px
              colors: colorSets[id % colorSets.length],
              trailLength: Math.random() * 120 + 140, // noticeably longer tails
              trailThickness: Math.random() * 0.6 + 0.9, // subtle variation in tail thickness
              glowIntensity: Math.random() * 1.8 + 2, // brighter glow per star
              trailOpacity: Math.random() * 0.2 + 0.75 // keep some variety in tail visibility
            };
          };
          
          const stars = [1, 2, 3, 4, 5].map(id => generateRandomStar(id));
          
          return stars.map((star) => {
            return (
              <div key={`shooting-star-${star.id}`}>
                {/* Main Star */}
                <div style={{
                  position: 'absolute',
                  top: `${star.startY}%`,
                  left: `${star.startX}%`,
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  background: star.colors[0],
                  borderRadius: '50%',
                  boxShadow: `0 0 ${star.size * star.glowIntensity}px ${star.colors[0]}, 0 0 ${star.size * star.glowIntensity * 1.8}px ${star.colors[1]}, 0 0 ${star.size * star.glowIntensity * 2.8}px ${star.colors[2]}`,
                  animation: `shootingStar${star.id} ${star.duration}s linear infinite`,
                  animationDelay: `${star.delay}s`,
                  zIndex: 2,
                  '--angle': `${star.angle}deg`,
                  '--startX': `${star.startX}vw`,
                  '--startY': `${star.startY}vh`,
                  '--endX': `${star.endX}vw`,
                  '--endY': `${star.endY}vh`,
                  filter: 'blur(0.3px)',
                  mixBlendMode: 'screen'
                }}>
                  {/* Trail effect */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    right: `${star.size}px`,
                    transform: 'translateY(-50%)',
                    transformOrigin: 'right center',
                    width: `${star.trailLength}px`,
                    height: `${Math.max(star.size * star.trailThickness, 2)}px`,
                    background: `linear-gradient(90deg, transparent 0%, ${star.colors[0]}33 30%, ${star.colors[1]}66 65%, ${star.colors[2]}cc 100%)`,
                    borderRadius: '1px',
                    animation: `shootingTrail${star.id} ${star.duration}s linear infinite`,
                    animationDelay: `${star.delay}s`,
                    opacity: star.trailOpacity,
                    filter: 'blur(1px)',
                    boxShadow: `0 0 25px ${star.colors[2]}80`
                  }} />
                </div>
              </div>
            );
          });
        })()}
        
      </div>
      {/* CSS Animations */}
      <style>{`
        @keyframes moonScroll {
          0% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(-20px) translateY(10px); }
          50% { transform: translateX(-40px) translateY(0); }
          75% { transform: translateX(-20px) translateY(-10px); }
          100% { transform: translateX(0) translateY(0); }
        }
        
        @keyframes starSparkle {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 1;
            transform: scale(1.5);
          }
        }
        
        @keyframes shootingStar1 {
          0% { 
            transform: translate(0, 0) rotate(var(--angle));
            opacity: 0;
          }
          10% { 
            opacity: 1;
          }
          90% { 
            opacity: 1;
          }
          100% { 
            transform: translate(calc(var(--endX) - var(--startX)), calc(var(--endY) - var(--startY))) rotate(var(--angle));
            opacity: 0;
          }
        }
        
        @keyframes shootingStar2 {
          0% { 
            transform: translate(0, 0) rotate(var(--angle));
            opacity: 0;
          }
          10% { 
            opacity: 1;
          }
          90% { 
            opacity: 1;
          }
          100% { 
            transform: translate(calc(var(--endX) - var(--startX)), calc(var(--endY) - var(--startY))) rotate(var(--angle));
            opacity: 0;
          }
        }
        
        @keyframes shootingStar3 {
          0% { 
            transform: translate(0, 0) rotate(var(--angle));
            opacity: 0;
          }
          10% { 
            opacity: 1;
          }
          90% { 
            opacity: 1;
          }
          100% { 
            transform: translate(calc(var(--endX) - var(--startX)), calc(var(--endY) - var(--startY))) rotate(var(--angle));
            opacity: 0;
          }
        }
        
        @keyframes shootingStar4 {
          0% { 
            transform: translate(0, 0) rotate(var(--angle));
            opacity: 0;
          }
          10% { 
            opacity: 1;
          }
          90% { 
            opacity: 1;
          }
          100% { 
            transform: translate(calc(var(--endX) - var(--startX)), calc(var(--endY) - var(--startY))) rotate(var(--angle));
            opacity: 0;
          }
        }
        
        @keyframes shootingStar5 {
          0% { 
            transform: translate(0, 0) rotate(var(--angle));
            opacity: 0;
          }
          10% { 
            opacity: 1;
          }
          90% { 
            opacity: 1;
          }
          100% { 
            transform: translate(calc(var(--endX) - var(--startX)), calc(var(--endY) - var(--startY))) rotate(var(--angle));
            opacity: 0;
          }
        }
        
        @keyframes shootingTrail1 {
          0% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
          10% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          90% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          100% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
        }
        
        @keyframes shootingTrail2 {
          0% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
          10% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          90% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          100% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
        }
        
        @keyframes shootingTrail3 {
          0% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
          10% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          90% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          100% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
        }
        
        @keyframes shootingTrail4 {
          0% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
          10% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          90% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          100% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
        }
        
        @keyframes shootingTrail5 {
          0% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
          10% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          90% { 
            opacity: 1;
            transform: translateY(-50%) scaleX(1);
          }
          100% { 
            opacity: 0;
            transform: translateY(-50%) scaleX(0);
          }
        }
        
      `}</style>
      
      {/* Header */}
      <div style={{
        maxWidth: '1200px',
        margin: '100px auto 40px auto',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <h1 style={{
          fontSize: '64px',
          fontWeight: 'bold',
          color: 'white',
          margin: '0 0 20px 0',
          textShadow: '3px 3px 6px rgba(0,0,0,0.5)',
          background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Events
        </h1>
        
        <p style={{
          fontSize: '20px',
          color: '#e5e7eb',
          marginBottom: '40px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
        }}>
          Browse and download photos from events Zee attends
        </p>

        {/* Search Bar */}
        <div style={{
          maxWidth: '500px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 10
        }}>
          <div style={{
            position: 'absolute',
            left: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }}>
            <Search size={20} />
          </div>
          
          <input
            type="text"
            placeholder="Search by date or event name..."
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            style={{
              width: '100%',
              padding: '15px 15px 15px 50px',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              color: 'white',
              fontSize: '16px',
              outline: 'none',
              transition: 'all 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Events Grid */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 10
      }}>
        {filteredEvents.length === 0 ? (
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '60px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '10px' }}>
              No events available yet
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '16px' }}>
              Check back later for event photo galleries!
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {filteredEvents.map(event => (
              <div
                key={event.id}
                onClick={() => {
                  setSelectedEvent(event);
                  const eventSlug = event.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                  navigate(`/events/${eventSlug}`);
                }}
                style={{
                  background: 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(251, 191, 36, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                }}
              >
                {/* Cover Image/Video */}
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  {event.image || event.coverImage ? (
                    <img
                      src={event.image || event.coverImage}
                      alt={event.name}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      width: '100%',
                      height: '200px',
                      background: 'linear-gradient(135deg, #374151, #1f2937)',
                      display: event.image || event.coverImage ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9ca3af',
                      fontSize: '16px'
                    }}
                  >
                    📸 Event Gallery
                  </div>
                </div>

                {/* Event Info */}
                <div style={{ padding: '20px' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '8px',
                    lineHeight: '1.3'
                  }}>
                    {event.name}
                  </h3>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#fbbf24' }}>
                      <Calendar size={14} />
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {new Date(event.date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#fbbf24' }}>
                      <Users size={14} />
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {event.photoCount || event.photos?.length || 0} Photos
                      </span>
                    </div>
                  </div>
                  
                  <p style={{
                    color: '#9ca3af',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {event.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Events;